import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import NewPricingPlans from '../components/NewPricingPlans';
import OptimizedRegistrationForm from '../components/OptimizedRegistrationForm';
import { registerShop, checkUserShopLimit } from '../services/shopService';
import { isAuthenticated, getCurrentUser } from '../services/authService';
import { loadStripe } from '@stripe/stripe-js';
import { Layout, Steps, Button, Result, Spin, Alert, Typography, Card, Row, Col, message } from 'antd';
import { SmileOutlined, SolutionOutlined, CreditCardOutlined, LoadingOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import api from '../services/api';

const { Content } = Layout;
const { Step } = Steps;
const { Title, Paragraph } = Typography;

// Initialize Stripe with error handling
const initializeStripe = async () => {
    const stripeKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
    if (!stripeKey) {
        console.error('Stripe Public Key not found in environment variables');
        return null;
    }

    try {
        return await loadStripe(stripeKey);
    } catch (error) {
        console.error('Error initializing Stripe:', error);
        return null;
    }
};

const stripePromise = initializeStripe();

const RegisterShopPage = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(0);
    const [selectedPlanData, setSelectedPlanData] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        ownerName: '',
        email: '',
        phone: '',
        address: '',
        postalCode: '',
        city: '',
        website: '',
        description: '',
        openingHours: {
            monday: { open: '09:00', close: '18:00' },
            tuesday: { open: '09:00', close: '18:00' },
            wednesday: { open: '09:00', close: '18:00' },
            thursday: { open: '09:00', close: '18:00' },
            friday: { open: '09:00', close: '18:00' },
            saturday: { open: '10:00', close: '16:00' },
            sunday: { open: '', close: '' },
        },
        brands: [],
        images: [],
        acceptTerms: false,
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});
    const [success, setSuccess] = useState(false);
    const [user, setUser] = useState(null);
    const [isUserAuthenticated, setIsUserAuthenticated] = useState(false);
    const [userShopCount, setUserShopCount] = useState(0);
    const [isDemoMode] = useState(
        process.env.REACT_APP_SKIP_PAYMENT === 'true' ||
        process.env.REACT_APP_DEBUG_MODE === 'true'
    );
    const [authCheckComplete, setAuthCheckComplete] = useState(false);

    useEffect(() => {
        const checkAuth = async () => {
            try {
                setAuthCheckComplete(false);
                setError(null);
                const authenticated = await isAuthenticated();
                setIsUserAuthenticated(authenticated);

                if (authenticated) {
                    const currentUser = await getCurrentUser();
                    setUser(currentUser);
                    const shopCount = currentUser?.shops?.length || 0;
                    setUserShopCount(shopCount);
                    if (shopCount >= 3) {
                        setError('Sie haben bereits die maximale Anzahl an Shops (3) registriert. Weiterleitung zum Dashboard...');
                        setTimeout(() => navigate('/dashboard'), 4000);
                    } else {
                        setFormData(prevData => ({
                            ...prevData,
                            email: currentUser.email || '',
                            ownerName: currentUser.name || ''
                        }));
                    }
                }
            } catch (error) {
                console.error('Auth check error:', error);
                setError('Fehler bei der Authentifizierungsprüfung. Bitte laden Sie die Seite neu.');
            } finally {
                setAuthCheckComplete(true);
            }
        };

        const performAuthCheckAndRedirect = async () => {
            const authenticated = await isAuthenticated();
            if (!authenticated) {
                message.info({
                    content: 'Bitte melden Sie sich an, um einen Shop zu registrieren.',
                    key: 'authRedirectMessage'
                });
                navigate('/login?redirect=/register');
            } else {
                await checkAuth();
            }
        };

        performAuthCheckAndRedirect();

    }, [navigate]);

    const handlePlanSelect = (selectedData) => {
        console.log('[RegisterShopPage] Plan ausgewählt:', selectedData);
        setSelectedPlanData(selectedData);
        setCurrentStep(1);
        setError(null);
    };

    const handleFormChange = useCallback((data) => {
        setFormData(prevData => ({
            ...prevData,
            ...data
        }));
    }, []);

    const validateForm = () => {
        if (!selectedPlanData) {
            setError('Bitte wählen Sie zuerst einen Tarif aus.');
            setCurrentStep(0);
            return false;
        }
        if (!formData.name || !formData.address || !formData.postalCode || !formData.city) {
            setError('Bitte füllen Sie mindestens Namen und Adressdaten aus.');
            setCurrentStep(1);
            return false;
        }
        if (!formData.acceptTerms) {
            setError('Bitte akzeptieren Sie die AGB und Datenschutzbestimmungen.');
            setCurrentStep(1);
            return false;
        }
        setFieldErrors({});
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }
        setSubmitting(true);
        setError(null);
        setFieldErrors({});
        message.loading({ content: 'Registrierung wird verarbeitet...', key: 'registerShop' });

        try {
            if (!isUserAuthenticated || !user?.id) {
                throw new Error('Authentifizierung erforderlich.');
            }

            const finalFormData = {
                ...formData,
                plan: selectedPlanData.id,
                subscriptionInterval: selectedPlanData.interval
            };
            
            const response = await api.post('/shops', finalFormData);

            if (!response.success || !response.sessionId) {
                if (response.errors) {
                    const serverErrors = response.errors.reduce((acc, err) => {
                        acc[err.param || 'general'] = err.msg;
                        return acc;
                    }, {});
                    setFieldErrors(serverErrors);
                    setError('Bitte überprüfen Sie Ihre Eingaben.');
                } else {
                    setError(response.message || 'Fehler bei der Shop-Erstellung.');
                }
                message.error({ content: `Fehler: ${error || 'Shop konnte nicht erstellt werden.'}`, key: 'registerShop' });
                return;
            }

            message.success({ content: 'Shop-Daten gespeichert! Weiterleitung zur Zahlung... ', key: 'registerShop' });
            const stripe = await stripePromise;
            if (!stripe) {
                throw new Error('Stripe konnte nicht initialisiert werden.');
            }

            const { error: stripeError } = await stripe.redirectToCheckout({
                sessionId: response.sessionId,
            });

            if (stripeError) {
                console.error("Stripe Weiterleitungsfehler:", stripeError);
                setError(`Fehler bei der Weiterleitung zur Zahlung: ${stripeError.message}`);
                message.error({ content: `Weiterleitungsfehler: ${stripeError.message}`, key: 'registerShop' });
            } else {
                message.destroy('registerShop');
            }

        } catch (err) {
            console.error("Fehler beim Registrieren/Bezahlen:", err);
            setError(err.message || 'Ein unerwarteter Fehler ist aufgetreten.');
            message.error({ content: `Fehler: ${err.message || 'Bitte versuchen Sie es erneut.'}`, key: 'registerShop' });
        } finally {
            setSubmitting(false);
        }
    };

    const goBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
            setError(null);
        }
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case 0:
                return (
                    <Card title="1. Tarif auswählen" bordered={false}>
                        <Paragraph>Wählen Sie den gewünschten Tarif und das Abrechnungsintervall für Ihren Shop.</Paragraph>
                        <NewPricingPlans 
                        onSelect={handlePlanSelect}
                        />
                    </Card>
                );
            case 1:
                return (
                    <Card title="2. Shop-Informationen eingeben" bordered={false}>
                        {selectedPlanData && (
                             <Alert
                                 message={`Gewählter Tarif: ${selectedPlanData.id === 'premium' ? 'Premium' : 'Professional'} (${selectedPlanData.interval === 'monthly' ? 'Monatlich' : 'Jährlich'})`}
                                 type="info"
                                 showIcon
                                 action={
                                     <Button size="small" type="text" onClick={() => setCurrentStep(0)}>
                                         Ändern
                                     </Button>
                                 }
                                 style={{ marginBottom: '20px' }}
                             />
                        )}
                        <OptimizedRegistrationForm 
                            formData={formData} 
                            onChange={handleFormChange} 
                            fieldErrors={fieldErrors} 
                        />
                    </Card>
                );
            case 2:
                return (
                    <Card title="3. Zusammenfassung & Zahlung" bordered={false}>
                        <Spin tip="Leite zur Zahlung weiter...">
                            <div style={{height: '100px'}} />
                        </Spin>
                    </Card>
                );
            default:
                return null;
        }
    };

    if (!authCheckComplete) {
        return (
            <Layout style={{ minHeight: 'calc(100vh - 184px)', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Spin size="large" tip="Prüfe Authentifizierung..." />
            </Layout>
        );
    }

    return (
        <Layout>
            <Helmet>
                <title>Shop registrieren | Snus Verzeichnis Deutschland</title>
                <meta name="description" content="Registrieren Sie Ihren Laden oder Kiosk im Snus Verzeichnis Deutschland und erreichen Sie neue Kunden." />
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>
            <Content style={{ padding: '50px', maxWidth: '1000px', margin: '0 auto' }}>
                <Title level={2} style={{ textAlign: 'center', marginBottom: '30px' }}>Neuen Shop registrieren</Title>
                
                {error && (
                    <Alert
                        message="Fehler"
                        description={error}
                        type="error"
                        showIcon
                        closable
                        style={{ margin: '24px 0' }}
                        onClose={() => setError(null)}
                    />
                )}
                
                <Steps current={currentStep} style={{ marginBottom: '30px' }}>
                    <Step title="Tarif wählen" icon={<CreditCardOutlined />} />
                    <Step title="Shop-Details" icon={<SolutionOutlined />} />
                    <Step title="Zahlung abschließen" icon={<SmileOutlined />} />
                </Steps>
                
                {renderStepContent()}

                <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'space-between' }}>
                    {currentStep === 1 && (
                        <Button onClick={goBack} disabled={submitting}>
                            Zurück (Tarifauswahl)
                        </Button>
                    )}
                    {currentStep === 1 && (
                        <Button 
                            type="primary" 
                            onClick={handleSubmit} 
                            loading={submitting} 
                            disabled={!formData.acceptTerms || !selectedPlanData}
                        >
                            Registrieren und Bezahlen
                        </Button>
                    )}
                </div>
            </Content>
        </Layout>
    );
};

export default RegisterShopPage;